import { useEffect, useState } from 'react'
import { useCronograma, type FaseItem, type FaseStatus } from '@hooks/useCronograma.js'
import type { BadgeVariant } from '@components/primitives/Badge.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { Badge } from '@components/primitives/Badge.js'
import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { MoneyInput } from '@components/primitives/MoneyInput.js'
import { Modal } from '@components/primitives/Modal.js'
import { Textarea } from '@components/primitives/Textarea.js'
import { CardListSkeleton, PageHeaderSkeleton } from '@components/primitives/Skeleton.js'
import { barFillStyle } from '@utils/barStyle.js'
import { formatCurrency, formatDate } from '@utils/format.js'
import { faseTemFotoProva } from '@services/gestao.js'
import { ROUTES } from '@utils/constants.js'
import { useNavigate } from 'react-router-dom'

const STATUS_LABEL: Record<FaseStatus, string> = {
  'nao-iniciada': 'Não iniciada',
  'em-andamento': 'Em andamento',
  concluida: 'Concluída',
  atrasada: 'Atrasada',
}

const STATUS_VARIANT: Record<FaseStatus, BadgeVariant> = {
  'nao-iniciada': 'default',
  'em-andamento': 'warning',
  concluida: 'success',
  atrasada: 'danger',
}

const AVANCOS = [0, 25, 50, 75, 100] as const

export function CronogramaPage() {
  const { itens, resumo, config, temDatas, isLoading, definirAvanco, editarFase, aplicarSugestao } =
    useCronograma()
  const [sugestaoOpen, setSugestaoOpen] = useState(false)
  const [faseEdit, setFaseEdit] = useState<FaseItem | null>(null)
  const [fecharSemFoto, setFecharSemFoto] = useState<FaseItem | null>(null)
  const navigate = useNavigate()

  async function tentarAvanco(f: FaseItem, pct: number) {
    if (pct === 100 && f.avancoPct < 100 && !(await faseTemFotoProva(f.faseId))) {
      setFecharSemFoto(f)
      return
    }
    await definirAvanco(f.faseId, pct)
  }

  if (isLoading) {
    return (
      <PageShell>
        <PageHeaderSkeleton label='Carregando cronograma…' />
        <section className='cf3-block'>
          <CardListSkeleton count={4} label='Carregando fases…' />
        </section>
      </PageShell>
    )
  }

  const atrasadas = itens.filter((f) => f.status === 'atrasada')

  return (
    <PageShell>
      <ModePageHeader
        title='Cronograma'
        subtitle='As 9 fases da obra com previsão, avanço e atraso. Atualize o avanço no fim de cada semana.'
      >
        <Button
          variant={temDatas ? 'secondary' : 'primary'}
          size='sm'
          onClick={() => setSugestaoOpen(true)}
        >
          {temDatas ? 'Refazer datas' : 'Sugerir datas'}
        </Button>
      </ModePageHeader>

      <section className='cf3-block' aria-label='Resumo do cronograma'>
        <div className='cf3-block__card cf3-crono-resumo'>
          <div className='cf3-crono-resumo__top'>
            <div>
              <span className='cf3-gestao-kpi__label'>Avanço geral</span>
              <p className='cf3-crono-resumo__pct'>{resumo.avancoGeral}%</p>
            </div>
            <div className='cf3-crono-resumo__meta'>
              {resumo.diasDeObra !== null && (
                <span className='cf3-crono-resumo__chip'>Dia {resumo.diasDeObra} de obra</span>
              )}
              {resumo.terminoPrevisto && (
                <span className='cf3-crono-resumo__chip'>
                  Término {formatDate(resumo.terminoPrevisto)}
                </span>
              )}
              {resumo.atrasoDias > 0 ? (
                <span className='cf3-crono-resumo__chip cf3-crono-resumo__chip--danger'>
                  {resumo.atrasoDias} {resumo.atrasoDias === 1 ? 'dia' : 'dias'} de atraso
                </span>
              ) : temDatas ? (
                <span className='cf3-crono-resumo__chip cf3-crono-resumo__chip--ok'>Em dia</span>
              ) : null}
            </div>
          </div>
          <div
            className='cf3-fin-budget-bar'
            role='progressbar'
            aria-valuenow={resumo.avancoGeral}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Avanço geral ${resumo.avancoGeral}%`}
          >
            <div className='cf3-fin-budget-bar__fill' style={barFillStyle(resumo.avancoGeral)} />
          </div>
          {!temDatas && (
            <p className='cf3-crono-resumo__hint'>
              Sem datas ainda. Toque em "Sugerir datas" para distribuir as fases a partir do início
              da obra. Você ajusta depois, fase a fase.
            </p>
          )}
          {atrasadas.length > 0 && (
            <p className='cf3-gestao-aviso cf3-gestao-aviso--danger' role='status'>
              {atrasadas.length === 1
                ? `Fase atrasada: ${atrasadas[0]!.label}`
                : `${atrasadas.length} fases passaram do fim previsto`}
            </p>
          )}
        </div>
      </section>

      <section className='cf3-block' aria-label='Fases da obra'>
        <ol className='cf3-crono'>
          {itens.map((f) => (
            <li
              key={f.faseId}
              className={`cf3-crono-fase cf3-crono-fase--${f.status}${resumo.faseAtualId === f.faseId ? ' cf3-crono-fase--atual' : ''}`}
            >
              <span className='cf3-crono-fase__marker' aria-hidden>
                {f.status === 'concluida' ? '✓' : f.ordem}
              </span>
              <div className='cf3-crono-fase__body'>
                <div className='cf3-crono-fase__top'>
                  <h3 className='cf3-crono-fase__title'>{f.label}</h3>
                  <Badge
                    label={STATUS_LABEL[f.status]}
                    variant={STATUS_VARIANT[f.status]}
                    size='sm'
                  />
                </div>
                {resumo.faseAtualId === f.faseId && (
                  <p className='cf3-crono-fase__desc'>{f.desc}</p>
                )}
                <p className='cf3-crono-fase__datas'>
                  {f.inicioPrevisto || f.fimPrevisto ? (
                    <>
                      {f.inicioPrevisto ? formatDate(f.inicioPrevisto) : '?'}
                      <span aria-hidden> → </span>
                      {f.fimPrevisto ? formatDate(f.fimPrevisto) : '?'}
                      {f.duracaoDias ? ` · ${f.duracaoDias} dias` : ''}
                      {f.atrasoDias > 0 ? ` · ${f.atrasoDias} dias de atraso` : ''}
                    </>
                  ) : (
                    'Sem datas previstas'
                  )}
                  {f.orcamentoPrevisto ? ` · ${formatCurrency(f.orcamentoPrevisto)}` : ''}
                </p>

                <div className='cf3-crono-fase__avanco'>
                  <div
                    className='cf3-segment cf3-segment--full'
                    role='radiogroup'
                    aria-label={`Avanço de ${f.label}`}
                  >
                    {AVANCOS.map((pct) => (
                      <button
                        key={pct}
                        type='button'
                        role='radio'
                        aria-checked={f.avancoPct === pct}
                        className={`cf3-segment__btn${f.avancoPct === pct ? ' cf3-segment__btn--active' : ''}`}
                        onClick={() => void tentarAvanco(f, pct)}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                  <button
                    type='button'
                    className='cf3-parcela__btn'
                    onClick={() => setFaseEdit(f)}
                    aria-label={`Editar datas de ${f.label}`}
                  >
                    Editar
                  </button>
                </div>

                {f.observacoes && <p className='cf3-crono-fase__obs'>{f.observacoes}</p>}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <SugestaoModal
        open={sugestaoOpen}
        dataInicioPadrao={config?.dataInicio ?? ''}
        onClose={() => setSugestaoOpen(false)}
        onAplicar={aplicarSugestao}
      />

      <FaseModal fase={faseEdit} onClose={() => setFaseEdit(null)} onSalvar={editarFase} />

      <Modal
        open={fecharSemFoto !== null}
        title='Fechar fase sem foto?'
        onClose={() => setFecharSemFoto(null)}
        actions={
          <div className='cf3-modal-actions'>
            <Button
              variant='secondary'
              onClick={() => {
                setFecharSemFoto(null)
                navigate(`${ROUTES.DIARIO}?foto=1`)
              }}
            >
              Tirar foto agora
            </Button>
            <Button
              variant='ghost'
              onClick={() => {
                if (!fecharSemFoto) return
                void definirAvanco(fecharSemFoto.faseId, 100)
                setFecharSemFoto(null)
              }}
            >
              Fechar mesmo assim
            </Button>
          </div>
        }
      >
        <p className='cf3-hoje__linha'>
          {fecharSemFoto
            ? `Nenhuma foto de ${fecharSemFoto.label} no diário. Sem prova, disputa vira palavra contra palavra.`
            : ''}
        </p>
      </Modal>
    </PageShell>
  )
}

interface SugestaoProps {
  open: boolean
  dataInicioPadrao: string
  onClose: () => void
  onAplicar: (dataInicio: string, duracaoDias: number) => Promise<void>
}

function SugestaoModal({ open, dataInicioPadrao, onClose, onAplicar }: SugestaoProps) {
  const [dataInicio, setDataInicio] = useState('')
  const [meses, setMeses] = useState(12)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!open) return
    setDataInicio(dataInicioPadrao || new Date().toISOString().slice(0, 10))
    setMeses(12)
  }, [open, dataInicioPadrao])

  const canSubmit = Boolean(dataInicio) && meses >= 3 && meses <= 48

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!canSubmit) return
    setSalvando(true)
    await onAplicar(dataInicio, Math.round(meses * 30.4))
    setSalvando(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      title='Sugerir datas por fase'
      onClose={onClose}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            type='submit'
            form='sugestao-form'
            loading={salvando}
            disabled={!canSubmit}
          >
            Aplicar
          </Button>
        </div>
      }
    >
      <form id='sugestao-form' className='cf3-gestao-form' onSubmit={handleSubmit}>
        <p className='cf3-gestao-fieldset__desc'>
          Distribui a duração total entre as 9 fases com pesos típicos de casa residencial
          (acabamento fino é a mais longa). Datas já digitadas serão substituídas.
        </p>
        <div className='cf3-gestao-form__row'>
          <Input
            label='Início da obra'
            type='date'
            value={dataInicio}
            required
            onChange={(e) => setDataInicio(e.target.value)}
          />
          <Input
            label='Duração total (meses)'
            type='number'
            value={String(meses)}
            hint='Casa de 100 a 200 m²: 10 a 18 meses'
            onChange={(e) => setMeses(Number(e.target.value) || 0)}
          />
        </div>
      </form>
    </Modal>
  )
}

interface FaseModalProps {
  fase: FaseItem | null
  onClose: () => void
  onSalvar: (
    faseId: string,
    patch: {
      inicioPrevisto?: string | undefined
      fimPrevisto?: string | undefined
      orcamentoPrevisto?: number | undefined
      observacoes?: string | undefined
    },
  ) => Promise<void>
}

function FaseModal({ fase, onClose, onSalvar }: FaseModalProps) {
  const [inicio, setInicio] = useState('')
  const [fim, setFim] = useState('')
  const [orcamento, setOrcamento] = useState(0)
  const [obs, setObs] = useState('')
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!fase) return
    setInicio(fase.inicioPrevisto ?? '')
    setFim(fase.fimPrevisto ?? '')
    setOrcamento(fase.orcamentoPrevisto ?? 0)
    setObs(fase.observacoes ?? '')
  }, [fase])

  const invalido = Boolean(inicio && fim && fim < inicio)

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!fase || invalido) return
    setSalvando(true)
    await onSalvar(fase.faseId, {
      inicioPrevisto: inicio || undefined,
      fimPrevisto: fim || undefined,
      orcamentoPrevisto: orcamento > 0 ? orcamento : undefined,
      observacoes: obs.trim() || undefined,
    })
    setSalvando(false)
    onClose()
  }

  return (
    <Modal
      open={fase !== null}
      title={fase ? `Fase ${fase.ordem}: ${fase.label}` : ''}
      onClose={onClose}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            type='submit'
            form='fase-form'
            loading={salvando}
            disabled={invalido}
          >
            Salvar
          </Button>
        </div>
      }
    >
      <form id='fase-form' className='cf3-gestao-form' onSubmit={handleSubmit}>
        {fase && <p className='cf3-gestao-fieldset__desc'>{fase.desc}</p>}
        <div className='cf3-gestao-form__row'>
          <Input
            label='Início previsto'
            type='date'
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
          />
          <Input
            label='Fim previsto'
            type='date'
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            {...(invalido ? { error: 'Fim antes do início' } : {})}
          />
        </div>
        <MoneyInput
          label='Orçamento previsto para a fase (opcional)'
          value={orcamento}
          hint='Ajuda a comparar o gasto real por fase.'
          onChange={setOrcamento}
        />
        <Textarea
          label='Observações'
          value={obs}
          rows={2}
          placeholder='Depende da chegada do aço, aguardar laudo…'
          onChange={(e) => setObs(e.target.value)}
        />
      </form>
    </Modal>
  )
}
